import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass, SmallTitleSpan } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultWMS1120_R01BomDto, WMS1120_R01BomDto } from '../Dto_WMS1120_R01';
import { useWMS1120_R01Store } from '../Store_WMS1120_R01';

const BOM_COLUMN_STATE_KEY = 'WMS1120_R01-bom-grid-column-state';

const applyBottomCenterAlign = <T,>(defs: ColDef<T>[], bottomLabelCols: Set<string>): ColDef<T>[] =>
	defs.map((def) => {
		if (!def?.colId || !bottomLabelCols.has(def.colId)) return def;
		return {
			...def,
			cellStyle: (params: { node?: { rowPinned?: string | null } }) =>
				params?.node?.rowPinned === 'bottom' ? { textAlign: 'center' } : null,
		};
	});

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1120_R01BomDto> | null>;
	onRowClick?: (data: WMS1120_R01BomDto) => void;
}

export const BottomTableBom_WMS1120_R01 = ({ gridRef, onRowClick }: Props) => {
	const {
		WMS1120_R01bomList,
		selectedWMS1120_R01Bom,
		setSelectedWMS1120_R01Bom,
		WMS1120_R01bomColumnState,
		setWMS1120_R01BomColumnState,
		WMS1120ScrollTop,
		setWMS1120ScrollTop,
	} = useWMS1120_R01Store();

	const [, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: WMS1120_R01bomList,
		defaultGridData: {} as WMS1120_R01BomDto,
		selectedRow: selectedWMS1120_R01Bom,
	});

	const baseColumnDefs: ColDef<WMS1120_R01BomDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '투입수량', colId: 'lotQty', field: 'lotQty', width: 100,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			valueFormatter: (p) => p.value?.toLocaleString() ?? '0',
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
		},
		{
			headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 150,
			headerClass: aggridHeaderClass,
		},
	], []);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1120_R01BomDto>[] = useMemo(() => {
		const merged = WMS1120_R01bomColumnState === null
			? baseColumnDefs
			: GridCustomColumnDefs<WMS1120_R01BomDto>({
				baseColumnDefs,
				columnState: WMS1120_R01bomColumnState,
				headerNames: {},
				fallbackHeaderClass: aggridHeaderClass,
			});
		return applyBottomCenterAlign(merged, new Set(['itemCd']));
	}, [WMS1120_R01bomColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRowData = useMemo(() => ([{
		itemCd: `${WMS1120_R01bomList.length.toLocaleString()}건`,
		lotQty: WMS1120_R01bomList.reduce((sum, item) => sum + (item.lotQty || 0), 0).toLocaleString(),
	}]), [WMS1120_R01bomList]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1120_R01BomDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		if (!e.data) return;
		setSelectedWMS1120_R01Bom(e.data);
		onRowClick?.(e.data);
	}, [setSelectedWMS1120_R01Bom, onRowClick]);

	return (
		<div className="h-full flex flex-col">
			<SmallTitleSpan>자재·재공품 투입(소비) 내역</SmallTitleSpan>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<WMS1120_R01BomDto>
					enableCellCalculation
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					initSelected={() => setSelectedWMS1120_R01Bom(undefined)}
					defaultRowData={defaultWMS1120_R01BomDto}
					baseColumnDefs={WMS1120_R01bomColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setWMS1120_R01BomColumnState}
					localStorageKey={BOM_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Touch<WMS1120_R01BomDto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<WMS1120_R01BomDto>({
							idKey: 'valueIndex',
							selectedList: selectedWMS1120_R01Bom?.valueIndex ? [selectedWMS1120_R01Bom.valueIndex] : [],
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRowData}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={BOM_COLUMN_STATE_KEY}
						setColumnState={setWMS1120_R01BomColumnState}
						scrollTop={WMS1120ScrollTop.bom}
						setScrollTop={(top) => setWMS1120ScrollTop('bom', top)}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
